import { UseFormReturn } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { trpc } from '~/api/trpc';
import type { BillingDraftFormData } from './BillingDraftCreator';
import { BillingDraftAddressFields } from './BillingDraftAddressFields';
import { Skeleton } from '@/components/ui/skeleton';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '~/lib/utils';

type BillingDraftBasicDetailsFormProps = {
    form: UseFormReturn<BillingDraftFormData>;
    preselectedCustomerId?: number;
    preselectedAppointmentId?: string;
};

const BillingDraftBasicDetailsForm = ({
    form,
    preselectedCustomerId,
    preselectedAppointmentId,
}: BillingDraftBasicDetailsFormProps) => {
    const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [appointmentSearchOpen, setAppointmentSearchOpen] = useState(false);

    const selectedCustomerId = form.watch('customerId');
    const selectedAppointmentId = form.watch('appointmentId');

    // Fetch stores
    const { data: stores, isLoading: storesLoading } = trpc.stores.getAllSimple.useQuery();

    // Fetch customers for search
    const { data: customersData, isLoading: customersLoading } = trpc.customers.getAll.useQuery({
        page: 0,
        pageSize: 20,
        filters: { name: customerSearchTerm },
    });

    // Fetch selected customer details
    const { data: selectedCustomer } = trpc.customers.getOne.useQuery(
        { id: selectedCustomerId },
        { enabled: selectedCustomerId > 0 }
    );

    const endOfToday = useMemo(() => {
        const date = new Date();
        date.setHours(23, 59, 59, 999);
        return date.toISOString();
    }, []);

    // Fetch customer's billable appointments (scheduled or completed, starting today or earlier)
    const { data: appointmentsData } = trpc.appointments.getByCustomerId.useQuery(
        {
            customerId: selectedCustomerId,
            statuses: ['SCHEDULED', 'COMPLETED'],
            startsBefore: endOfToday,
        },
        { enabled: selectedCustomerId > 0 }
    );

    // Fetch selected appointment details
    const { data: selectedAppointment } = trpc.appointments.getOne.useQuery(
        { id: selectedAppointmentId || '' },
        { enabled: !!selectedAppointmentId }
    );

    const appointmentOptions = useMemo(() => {
        const options = appointmentsData ?? [];
        if (selectedAppointment && !options.some((apt) => apt.id === selectedAppointment.id)) {
            return [selectedAppointment, ...options];
        }
        return options;
    }, [appointmentsData, selectedAppointment]);

    // When appointment is selected, auto-fill customer and store if not set
    useEffect(() => {
        if (selectedAppointment) {
            if (!selectedCustomerId || selectedCustomerId === 0) {
                form.setValue('customerId', selectedAppointment.customerId);
            }
            if (selectedAppointment.storeId) {
                form.setValue('storeId', selectedAppointment.storeId);
            }
        }
    }, [selectedAppointment, selectedCustomerId, form]);

    return (
        <Form {...form}>
            <div className='space-y-6'>
                {/* Customer and Store Selection */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* Customer Selector */}
                    <FormField
                        control={form.control}
                        name='customerId'
                        render={({ field }) => (
                            <FormItem className='flex flex-col'>
                                <FormLabel>Kunde *</FormLabel>
                                <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                                    <PopoverTrigger asChild>
                                        <FormControl>
                                            <Button
                                                variant='outline'
                                                role='combobox'
                                                aria-expanded={customerSearchOpen}
                                                className='w-full justify-between'
                                                disabled={!!preselectedCustomerId}
                                            >
                                                {form.watch('customerName') || 'Kunde auswählen...'}
                                                <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                                            </Button>
                                        </FormControl>
                                    </PopoverTrigger>
                                    <PopoverContent className='w-[400px] p-0'>
                                        <Command>
                                            <CommandInput
                                                placeholder='Kunde suchen...'
                                                value={customerSearchTerm}
                                                onValueChange={setCustomerSearchTerm}
                                            />
                                            <CommandList>
                                                <CommandEmpty>Kein Kunde gefunden.</CommandEmpty>
                                                <CommandGroup>
                                                    {customersData?.data.map((customer) => (
                                                        <CommandItem
                                                            key={customer.id}
                                                            value={`${customer.firstName} ${customer.lastName} ${customer.email}`}
                                                            onSelect={() => {
                                                                field.onChange(customer.id);
                                                                setCustomerSearchOpen(false);
                                                            }}
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    'mr-2 h-4 w-4',
                                                                    field.value === customer.id ? 'opacity-100' : 'opacity-0'
                                                                )}
                                                            />
                                                            <div>
                                                                <div className='font-medium'>
                                                                    {customer.firstName} {customer.lastName}
                                                                </div>
                                                                {customer.email && (
                                                                    <div className='text-sm text-muted-foreground'>
                                                                        {customer.email}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Store Selector */}
                    <FormField
                        control={form.control}
                        name='storeId'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Filiale *</FormLabel>
                                <Select
                                    value={field.value ? String(field.value) : ''}
                                    onValueChange={(value) => field.onChange(parseInt(value))}
                                >
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder='Filiale auswählen...' />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {storesLoading ? (
                                            <div className='p-2'>
                                                <Skeleton className='h-8 w-full' />
                                            </div>
                                        ) : (
                                            stores?.map((store) => (
                                                <SelectItem key={store.id} value={String(store.id)}>
                                                    {store.name}
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                {/* Appointment Selection */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <FormField
                        control={form.control}
                        name='appointmentId'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Termin</FormLabel>
                                <Select
                                    value={field.value || 'none'}
                                    onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                                    disabled={!!preselectedAppointmentId || selectedCustomerId === 0}
                                >
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder='Termin auswählen...' />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        <SelectItem value='none'>Kein Termin</SelectItem>
                                        {appointmentOptions.map((apt) => (
                                            <SelectItem key={apt.id} value={apt.id}>
                                                {apt.title} - {new Date(apt.steps?.[0]?.dateFrom || '').toLocaleDateString('de-DE')}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <Separator />

                {/* Dates */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    <FormField
                        control={form.control}
                        name='treatmentDate'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Behandlungsdatum *</FormLabel>
                                <FormControl>
                                    <Input type='date' {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name='billingDraftDate'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Entwurfsdatum *</FormLabel>
                                <FormControl>
                                    <Input type='date' {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name='dueDate'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Fälligkeitsdatum *</FormLabel>
                                <FormControl>
                                    <Input type='date' {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <Separator />

                {/* Address Cards */}
                <BillingDraftAddressFields control={form.control} />
            </div>
        </Form>
    );
};

export default BillingDraftBasicDetailsForm;
